<template>
  <div class="input-group-container" v-bind="$attrs">
    <label v-if="label" :for="id" class="input-label">{{ label }}</label>
    <div class="input-group">
      <span
        v-for="(addon, index) in prefixAddons"
        :key="'prefix-' + index"
        class="input-group-addon"
      >
        <slot :name="'prefix-icon-' + index" class="input-icon">{{
          addon
        }}</slot>
      </span>
      <!-- Hidden input to confuse autofill heuristics -->
      <input type="text" style="display: none" />
      <input
        type="text"
        :id="id"
        :name="randomName"
        :value="displayValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :class="['input-field', { 'p-invalid': validation?.$error || error }]"
        :aria-describedby="`${id}-help`"
        autocomplete="new-password"
        @input="handleInput"
        @paste="handlePaste"
      />
      <span
        v-for="(addon, index) in suffixAddons"
        :key="'suffix-' + index"
        class="input-group-addon"
        @click="handleAddonClick(index)"
      >
        <slot :name="'suffix-icon-' + index" class="input-icon">{{
          addon
        }}</slot>
        <span
          v-if="showCopied && index === suffixAddons.length - 1"
          class="copied-tooltip"
          >Copied</span
        >
      </span>
    </div>
    <span v-if="error" :id="`${id}-help`" class="error-message text-left mt-2">
      {{ error }}
    </span>
    <span
      v-if="
        validation?.$error &&
        validation.required &&
        validation.required.$invalid
      "
      :id="`${id}-help`"
      class="error-message text-left mt-2"
    >
      {{ validation.required.$message.replace("Value", PropName) }}
    </span>
    <span
      v-else-if="
        validation?.$error &&
        validation.requiredIf &&
        validation.requiredIf.$invalid
      "
      :id="`${id}-help`"
      class="error-message text-left mt-2"
    >
      {{ validation.requiredIf.$message.replace("value", PropName) }}
    </span>
    <span
      v-else-if="
        validation?.$error && validation.email && validation.email.$invalid
      "
      :id="`${id}-help`"
      class="error-message text-left mt-2"
    >
      {{ validation.email.$message }}
    </span>
    <span
      v-else-if="
        validation?.$error && validation.numeric && validation.numeric.$invalid
      "
      :id="`${id}-help`"
      class="error-message text-left mt-2"
    >
      {{ validation.numeric.$message }}
    </span>
  </div>
</template>

<script>
import { sanitizeInput } from "@/utils/sanitize";

export default {
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      default: "",
    },
    modelValue: {
      type: [String, Number],
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: "",
    },
    validation: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    PropName: {
      type: String,
      default: "",
    },
    id: {
      type: String,
      default: "",
    },
    prefixAddons: {
      type: Array,
      default: () => [],
    },
    suffixAddons: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      actualValue: this.modelValue,
      isMasked: true,
      showCopied: false,
    };
  },
  computed: {
    randomName() {
      return `input-${Math.random().toString(36).substring(2)}`;
    },
    displayValue() {
      return this.isMasked
        ? "•".repeat(this.actualValue?.length)
        : this.actualValue;
    },
  },
  watch: {
    modelValue(newValue) {
      this.actualValue = newValue;
    },
  },
  methods: {
    handleInput(event) {
      const value = event?.target?.value;
      if (value == null || typeof value !== "string") {
        this.$emit("update:modelValue", "");
        this.actualValue = "";
        return;
      }

      let processedValue;
      if (this.isMasked) {
        const lastChar =
          typeof this.actualValue === "string" && this.actualValue.length > 0
            ? this.actualValue.slice(-1)
            : "";

        processedValue = value
          .replace(/\*/g, "")
          .padEnd(value.length, lastChar);
      } else {
        processedValue = value;
      }

      // Remove dangerous tags like <script> but keep < character and safe HTML
      const sanitizedValue = sanitizeInput(processedValue);
      this.actualValue = sanitizedValue;
      this.$emit("update:modelValue", sanitizedValue);
    },
    handlePaste(event) {
      event.preventDefault();
      const pastedText = (event.clipboardData || window.clipboardData).getData('text');
      const sanitizedText = sanitizeInput(pastedText);
      
      const input = event.target;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const currentValue = this.actualValue || '';
      
      const newValue = currentValue.substring(0, start) + sanitizedText + currentValue.substring(end);
      
      this.actualValue = newValue;
      this.$emit("update:modelValue", newValue);
    },
    handleAddonClick(index) {
      if (this.suffixAddons[index] === "eye") {
        this.isMasked = !this.isMasked;
        this.$emit("toggle-mask", this.isMasked);
      } else if (this.suffixAddons[index] === "copy") {
        if (this.actualValue) {
          navigator.clipboard
            .writeText(this.actualValue)
            .then(() => {
              this.showCopied = true;

              setTimeout(() => {
                this.showCopied = false;
              }, 1500);
              this.$emit("copy", this.actualValue);
            })
            .catch((err) => {
              console.error("Copy failed:", err);
            });
        } else {
          console.log("No value to copy");
        }
      }
    },
  },
};
</script>

<style scoped>
.input-group-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
  position: relative;
}

.input-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #333;
}

.input-group {
  display: flex;
  align-items: stretch;
  border: 1px solid #d3d3d3;
  border-radius: 4px;
  overflow: visible;
  position: relative;
}

.input-field {
  flex: 1;
  padding: 10px;
  border: none;
  font-size: 16px;
  outline: none;
  color: #21211f;
  font-weight: 500;
  line-height: 1;
  font-family: Arial, sans-serif;
}

.input-field::placeholder {
  color: #888;
  font-weight: normal;
  font-family: Arial, sans-serif;
}

.input-field:enabled:focus {
  outline: 0 none;
  outline-offset: 0;
  box-shadow: none;
  border: none;
}

.input-group:has(.input-field:enabled:focus) {
  border: #c0c0bc;
  outline: 2px solid #222222;
  border-style: double;
}

.input-group-addon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0.5rem;
  color: #888;
  pointer-events: auto;
  position: relative;
  transition: background-color 0.2s;
}

.input-group-addon:hover {
  background-color: #f5f5f5;
}

.input-icon {
  font-size: 0.875rem;
}

.error-message {
  color: #dc3545;
  font-size: 0.75rem;
}

.text-left {
  text-align: left;
}

.mt-2 {
  margin-top: 0.5rem;
}

.copied-tooltip {
  position: absolute;
  top: -35px;
  left: 50%;
  transform: translateX(-50%);
  background: #000000;
  color: white;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: nowrap;
  animation: fadeInOut 2.5s ease-in-out;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  pointer-events: none;
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
</style>